import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  markResolved,
  SupportTicketDetailApi,
} from "src/DAL/SupportTicket/SupportTicket";
import { htmlDecode } from "src/utils/convertHtml";
import CustomConfirmation from "src/components/GeneralComponents/CustomConfirmation";
import ChatCard from "./ChatCard";
import { gr } from "src/design-system/tokens";
import GrPage from "src/design-system/components/GrPage";
import icons from "src/design-system/icons";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrButton from "src/design-system/components/GrButton";
import GrIcon from "src/design-system/components/GrIcon";
import GrStatusPill from "src/design-system/components/GrStatusPill";
import GrLoader from "src/design-system/components/GrLoader";
import GrCard from "src/design-system/components/GrCard";
import GrAttachments from "src/design-system/components/GrAttachments";
import GrImageDialog from "src/design-system/components/GrImageDialog";
import Box from "@mui/material/Box";
import { savedFileTiles, ticketDate, ticketStatus } from "./ticketHelpers";

export default function SupportTicketDetail() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const { state } = useLocation();
  const [supportTicketData, setSupportTicketData] = useState([]);
  const [openResolved, setOpenResolved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState("");
  const handleMarkResolvedPopup = (value) => {
    setOpenResolved(true);
  };

  const handleNavigate = () => {
    navigate("/support-ticket", {
      state: state?.tabValue ? state?.tabValue : 0,
    });
  };

  const getSupportTicketDetail = async () => {
    const result = await SupportTicketDetailApi(params.id);
    if (result.code === 200) {
      setSupportTicketData(result.support_ticket);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      navigate(`/support-ticket`);
    }
  };

  const handleMarkResolved = async () => {
    setOpenResolved(false);
    setIsLoading(true);
    const result = await markResolved(params.id);
    if (result.code === 200) {
      // Wait for the fresh ticket, so the old open state does not flash back meanwhile.
      await getSupportTicketDetail(result.support_ticket);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      navigate(`/support-ticket`);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSupportTicketDetail();
  }, []);

  // Opened from the list, the ticket row comes along (router state): its subject, description, facts
  // and files show at once while the detail loads, so the page does not flash a spinner and jump.
  // Opened from a link (no row), the page waits for the detail as before.
  const ticket = supportTicketData?._id ? supportTicketData : state?.value;
  if (!ticket) {
    return <GrLoader sx={{ py: gr.space[12] }} />;
  }

  const status = ticketStatus(ticket);
  const ticketFiles = savedFileTiles(ticket.ticket_images);
  // Only from the fresh detail (not the list row shown while loading): a ticket resolved since must
  // not offer Mark Resolved again.
  const isOpen = !!supportTicketData?._id && supportTicketData.ticket_status === 0;

  // Ticket facts in one line under the subject (GitHub / Jira pattern): status, number, dates.
  const meta = [
    ticket.reference_number ? `#${ticket.reference_number}` : null,
    ticket.support_ticket_date
      ? `Created ${ticketDate(ticket.support_ticket_date)}`
      : null,
    ticket.ticket_status === 1 && ticket.resolve_date
      ? `Resolved ${ticketDate(ticket.resolve_date)}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <GrPage sx={{ pb: gr.space[8] }}>
      <GrPageHeader
        onBack={handleNavigate}
        backLabel="Back to support tickets"
        title={htmlDecode(ticket.subject)}
        // Mark Resolved on the title line, at the right edge (the title column is the full width).
        trailing={
          isOpen ? (
            <Box sx={{ ml: "auto" }}>
              <GrButton
                variant="outline"
                startIcon={<GrIcon icon={icons.check} />}
                onClick={handleMarkResolvedPopup}
              >
                Mark Resolved
              </GrButton>
            </Box>
          ) : null
        }
        // Status + facts, then what the member wrote when opening the ticket: all aligned with the title.
        subtitle={
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                columnGap: gr.space[3],
                rowGap: gr.space[1],
              }}
            >
              <GrStatusPill status={status.pill}>{status.text}</GrStatusPill>
              <Box component="span" sx={{ fontSize: gr.font.size.sm }}>
                {meta}
              </Box>
            </Box>
            <Box
              component="p"
              sx={{
                m: 0,
                mt: gr.space[4],
                color: gr.color.ink,
                lineHeight: gr.font.lineHeight.normal,
                whiteSpace: "pre-line",
                overflowWrap: "anywhere",
              }}
            >
              {htmlDecode(ticket.description)}
            </Box>
            <GrAttachments
              files={ticketFiles}
              onOpen={(index) => setImageUrl(ticketFiles[index].href)}
              sx={{ mt: ticketFiles.length ? gr.space[4] : 0 }}
            />
          </>
        }
      />
      {isLoading || !supportTicketData?._id ? (
        // The conversation loads inside its card, in about the space it will take.
        <GrCard as="section" aria-label="Conversation">
          <GrLoader
            sx={{
              minHeight: `calc(${gr.space[12]} * 5)`,
              alignItems: "center",
            }}
          />
        </GrCard>
      ) : (
        <ChatCard
          comments={supportTicketData.comment}
          ticket_id={supportTicketData._id}
          list_data={getSupportTicketDetail}
          supportTicketData={supportTicketData}
        />
      )}
      <CustomConfirmation
        open={openResolved}
        setOpen={setOpenResolved}
        title={"Are you sure you want to mark this ticket as resolved?"}
        handleAgree={handleMarkResolved}
      />
      <GrImageDialog
        open={!!imageUrl}
        onClose={() => setImageUrl("")}
        src={imageUrl}
      />
    </GrPage>
  );
}
